// 🎨 향상된 등급 선택 컴포넌트
// 요구사항에 맞는 최신 UI/UX 디자인

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Fade,
  Collapse,
  useTheme,
  useMediaQuery,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Info,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from '@mui/icons-material';
import { DeviceGrade, GradePriceInfo } from '../../types/gradeSystem';
import { getGradeColor, getGradeLabel, getGradeDescription, getGradeChecklist } from '../../utils/gradePriceUtils';
import { getGradesForCategory } from '../../constants/grades';
import GradeCriteriaGuideDialog, { GRADE_DETAILS } from './common/GradeCriteriaGuideDialog';

interface EnhancedGradeSelectionProps {
  selectedGrade: DeviceGrade | null;
  onGradeSelect: (grade: DeviceGrade) => void;
  gradePrices?: GradePriceInfo;
  deviceModel?: string;
  deviceStorage?: string;
  category?: string;
}

// GRADE_DETAILS는 common/GradeCriteriaGuideDialog에서 import

// 고급스러운 가격 정보 컴포넌트 (통합 버전)
const PremiumPriceDisplay: React.FC<{
  gradePrices?: GradePriceInfo;
  selectedGrade: DeviceGrade | null;
  deviceModel?: string;
  deviceStorage?: string;
}> = ({ gradePrices, selectedGrade, deviceModel, deviceStorage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isAnimating, setIsAnimating] = useState(false);

  // 가격 변경 시 애니메이션 효과
  React.useEffect(() => {
    if (selectedGrade && gradePrices?.[selectedGrade]) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [selectedGrade, gradePrices]);

  if (!gradePrices || !selectedGrade || !gradePrices[selectedGrade]) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              등급을 선택하면 예상 매입가를 확인할 수 있습니다
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const currentPrice = gradePrices[selectedGrade];
  const grades: DeviceGrade[] = ['A', 'B', 'C', 'D', 'E']; // PremiumPriceDisplay uses all grades for price calc
  const validPrices = grades
    .map(grade => ({ grade, price: gradePrices[grade] }))
    .filter(item => item.price !== null) as Array<{ grade: DeviceGrade; price: number }>;

  const minPrice = Math.min(...validPrices.map(item => item.price));
  const maxPrice = Math.max(...validPrices.map(item => item.price));
  const priceRange = maxPrice - minPrice;

  return (
    <Card sx={{ borderRadius: { xs: 2, sm: 3 }, boxShadow: 2, overflow: 'hidden' }}>
      <CardContent sx={{ p: 0 }}>

        {/* 메인 콘텐츠 */}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* 기기 정보 섹션 - 1줄 레이아웃 */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            py: 1,
            px: 2,
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            {/* 등급 아이콘 */}
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              backgroundColor: `${getGradeColor(selectedGrade)}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Typography
                variant="body2"
                sx={{
                  color: getGradeColor(selectedGrade),
                  fontWeight: 700,
                  fontSize: '0.875rem'
                }}
              >
                {selectedGrade}
              </Typography>
            </Box>

            {/* 기기 정보 - 1줄로 통합 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <Typography variant={isMobile ? 'body2' : 'subtitle2'} sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {deviceModel || 'iPhone'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                {deviceStorage || '용량 정보'}
              </Typography>
            </Box>

            {/* 등급 Chip */}
            <Chip
              label={GRADE_DETAILS[selectedGrade].label}
              size={isMobile ? 'small' : 'small'}
              sx={{
                backgroundColor: getGradeColor(selectedGrade),
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                height: { xs: 22, sm: 24 },
                flexShrink: 0,
              }}
            />
          </Box>

          {/* 구분선 */}
          <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

          {/* 가격 강조 섹션 */}
          <Box sx={{
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            py: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
          }}>
            {/* 가격 애니메이션 컨테이너 */}
            <Box
              sx={{
                opacity: isAnimating ? 0.7 : 1,
                transform: isAnimating ? 'scale(0.95)' : 'scale(1)',
                transition: 'all 0.3s ease-in-out',
              }}
            >
              {/* 메인 가격 */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1, mb: { xs: 1.5, sm: 2 } }}>
                <Typography
                  variant={isMobile ? 'h4' : 'h3'}
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    fontFamily: 'tabular-nums',
                    lineHeight: 1,
                  }}
                >
                  {currentPrice.toLocaleString()}
                </Typography>
                <Typography variant={isMobile ? 'body1' : 'h6'} color="text.secondary" sx={{ fontWeight: 500 }}>
                  원
                </Typography>
              </Box>

              {/* 부가 정보 */}
              <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary" sx={{ fontWeight: 500 }}>
                {GRADE_DETAILS[selectedGrade].description}
              </Typography>
            </Box>

            {/* 애니메이션 오버레이 */}
            {isAnimating && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(90deg, transparent 0%, rgba(25, 118, 210, 0.1) 50%, transparent 100%)',
                  animation: 'shimmer 0.6s ease-in-out',
                  '@keyframes shimmer': {
                    '0%': { transform: 'translateX(-100%)' },
                    '100%': { transform: 'translateX(100%)' },
                  },
                }}
              />
            )}
          </Box>

          {/* 구분선 */}
          <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

          {/* 등급별 가격 범위 카드들 */}
          {/* ⚠️ [UI 개선] 카드 높이 동일하게 맞춤 - height: '100%' + display: 'flex' 적용 */}
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>

            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
              {grades.map((grade) => {
                const price = gradePrices[grade];
                if (!price) return null;

                const isSelected = selectedGrade === grade;
                const percentage = priceRange > 0 ? ((price - minPrice) / priceRange) * 100 : 50;

                return (
                  <Grid item xs={6} sm={3} key={grade} sx={{ display: 'flex' }}>
                    <Card
                      sx={{
                        border: isSelected ? `2px solid ${getGradeColor(grade)}` : '1px solid #e0e0e0',
                        backgroundColor: isSelected ? `${getGradeColor(grade)}10` : 'white',
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: 'relative',
                        transition: 'all 0.2s ease-in-out',
                        width: '100%', // 전체 너비 사용
                        height: '100%', // 동일한 높이 유지
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateY(-2px)',
                          border: `2px solid ${getGradeColor(grade)}`,
                          backgroundColor: `${getGradeColor(grade)}15`,
                        },
                      }}
                    >
                      <CardContent 
                        sx={{ 
                          p: { xs: 1.5, sm: 2 }, 
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1, // 남은 공간 채우기
                          justifyContent: 'space-between', // 내용물 균등 배치
                        }}
                      >
                        {/* 상단: 등급 라벨 + 가격 */}
                        <Box>
                          {/* 등급 라벨 */}
                          <Chip
                            label={GRADE_DETAILS[grade].label}
                            size="small"
                            sx={{
                              backgroundColor: getGradeColor(grade),
                              color: 'white',
                              fontWeight: 600,
                              mb: { xs: 1, sm: 1.5 },
                              width: '100%',
                            }}
                          />

                          {/* 가격 */}
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: isSelected ? getGradeColor(grade) : 'text.primary',
                              fontFamily: 'tabular-nums',
                              mb: { xs: 0.5, sm: 1 },
                            }}
                          >
                            {price.toLocaleString()}원
                          </Typography>
                        </Box>

                        {/* 하단: 가격 바 */}
                        <Box
                          sx={{
                            height: 4,
                            backgroundColor: '#e0e0e0',
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${percentage}%`,
                              backgroundColor: getGradeColor(grade),
                              borderRadius: 2,
                              transition: 'all 0.3s ease-in-out',
                            }}
                          />
                        </Box>

                        {/* 선택 표시 */}
                        {isSelected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              backgroundColor: getGradeColor(grade),
                              borderRadius: '50%',
                              width: { xs: 18, sm: 20 },
                              height: { xs: 18, sm: 20 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckCircle sx={{ fontSize: { xs: 11, sm: 12 }, color: 'white' }} />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* 하단 구분선 및 안내 */}
          <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
          {/* 하단 안내 바 */}
          <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{
                backgroundColor: '#fff3cd',
                borderRadius: 2,
                border: '1px solid #ffeaa7',
                p: { xs: 1.5, sm: 2 },
                textAlign: 'center',
              }}>
                <Typography variant={isMobile ? 'body2' : 'body2'} color="text.secondary" sx={{ fontWeight: 500 }}>
                  ⚠️ 실제 검수 결과에 따라 최종 등급과 금액이 달라질 수 있습니다
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </CardContent>
    </Card>
  );
};

// GradeCriteriaGuideDialog는 common/GradeCriteriaGuideDialog에서 import

const EnhancedGradeSelection: React.FC<EnhancedGradeSelectionProps> = ({
  selectedGrade,
  onGradeSelect,
  gradePrices,
  deviceModel,
  deviceStorage,
  category,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showCriteriaDialog, setShowCriteriaDialog] = useState(false);
  const [previousPrices, setPreviousPrices] = useState<GradePriceInfo | null>(null);
  const [isGradeSectionCollapsed, setIsGradeSectionCollapsed] = useState(false);

  const grades: DeviceGrade[] = getGradesForCategory(category) as DeviceGrade[];

  // 가격 변화 추적
  React.useEffect(() => {
    if (gradePrices) {
      setPreviousPrices(gradePrices);
    }
  }, [gradePrices]);

  // 등급 선택 시 섹션 접기
  React.useEffect(() => {
    if (selectedGrade) {
      setIsGradeSectionCollapsed(true);
    }
  }, [selectedGrade]);

  // 현재 선택된 등급의 가격
  const currentPrice = selectedGrade ? gradePrices?.[selectedGrade] : null;

  return (
    <Box sx={{ p: 1.5 }}>
      {/* 상단 헤더 섹션 */}
      <Card sx={{ mb: { xs: 2, sm: 2.5 }, borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            mb: { xs: 1, sm: 1.5 }
          }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                휴대폰 상태(등급)를 선택해주세요
              </Typography>
              <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                사진과 문구를 참고해 본인 상태에 가장 가까운 등급을 선택하세요
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'small'}
              startIcon={<Info />}
              onClick={() => setShowCriteriaDialog(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                alignSelf: { xs: 'flex-end', sm: 'initial' },
                px: { xs: 1.25, sm: 1.75 },
                py: { xs: 0.5, sm: 0.75 },
              }}
            >
              등급 기준 자세히 보기
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 등급 선택 섹션 */}
      <Card sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 1 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <Chip label="등급 선택" color="primary" sx={{ mr: 2, fontWeight: 600, height: { xs: 22, sm: 28 }, flexShrink: 0 }} />
              <Typography
                variant={isMobile ? 'subtitle1' : 'h6'}
                sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                휴대폰 상태(등급)를 선택해주세요
              </Typography>
            </Box>

            {/* 선택된 등급 표시 및 접기/펼치기 버튼 */}
            {selectedGrade && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={`선택됨: ${GRADE_DETAILS[selectedGrade].label}`}
                  sx={{
                    backgroundColor: getGradeColor(selectedGrade),
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Button
                  size="small"
                  onClick={() => setIsGradeSectionCollapsed(!isGradeSectionCollapsed)}
                  sx={{ textTransform: 'none', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  {isGradeSectionCollapsed ? '다시 선택' : '접기'}
                </Button>
              </Box>
            )}
          </Box>

            <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary" sx={{ mb: { xs: 2, sm: 3 } }}>
            사진과 문구를 참고해 본인 상태에 가장 가까운 등급을 선택하세요
          </Typography>

          {/* 등급 카드들 - 접기/펼치기 가능 */}
          <Collapse in={!isGradeSectionCollapsed}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {grades.map((grade) => (
                <Card
                  key={grade}
                  sx={{
                    cursor: 'pointer',
                    border: selectedGrade === grade ? `2px solid ${getGradeColor(grade)}` : '1px solid #e0e0e0',
                    backgroundColor: selectedGrade === grade ? `${getGradeColor(grade)}10` : 'white',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                      border: `2px solid ${getGradeColor(grade)}`,
                      backgroundColor: `${getGradeColor(grade)}15`,
                    },
                    transition: 'all 0.2s ease-in-out',
                    position: 'relative',
                    borderRadius: 2,
                  }}
                  onClick={() => onGradeSelect(grade)}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                          label={GRADE_DETAILS[grade].label}
                          sx={{
                            backgroundColor: getGradeColor(grade),
                            color: 'white',
                            fontWeight: 600,
                            minWidth: { xs: 68, sm: 80 },
                          }}
                        />
                        <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary">
                          {getGradeDescription(grade)}
                        </Typography>
                      </Box>

                      {/* 선택 표시 */}
                      {selectedGrade === grade && (
                        <Box
                          sx={{
                            backgroundColor: getGradeColor(grade),
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* 가격 정보 섹션 */}
      <Card sx={{ mb: 2.5, borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Chip label="가격 정보" color="secondary" sx={{ mr: 2, fontWeight: 600 }} />
            <Typography variant={isMobile ? 'subtitle2' : 'h6'} sx={{ fontWeight: 600 }}>
              선택한 등급의 예상 매입가
            </Typography>
          </Box>

          {/* 통합된 가격 표시 */}
          <PremiumPriceDisplay
            gradePrices={gradePrices}
            selectedGrade={selectedGrade}
            deviceModel={deviceModel}
            deviceStorage={deviceStorage}
          />
        </CardContent>
      </Card>



      {/* 등급 기준 자세히 보기 모달 */}
      <GradeCriteriaGuideDialog
        open={showCriteriaDialog}
        onClose={() => setShowCriteriaDialog(false)}
        selectedGrade={selectedGrade}
        deviceType={category}
      />
    </Box>
  );
};

export default EnhancedGradeSelection;
